import { Tooltip } from '@mui/material';
import { useScrollSync } from '@/hooks';

import { formatToLocalDateTimeShort } from '../../../../../utils/dateFormatter';

import { PriceCell } from '../PriceCell';
import { SalesCell } from '../SalesCell';

import './RegionalTable.css';
import { RegionalTableProps } from './RegionalTable.types.ts';
import { useMemo } from 'react';

/**
 * Renders a regional pricing table showing OKKO reference prices and competitor comparisons
 * Displays brand names, sales data, and fuel prices across different fuel types
 *
 * @param props - The component props
 * @returns JSX element containing the complete regional pricing table
 */
export function RegionalTable({
  brands,
  fuelTypes,
  formatPrice,
  priceType,
  isMobile = false,
  regionKey = '',
  lastAuthor,
  lastUploadDatetime,
  hideSales = false
}: RegionalTableProps) {
  const { headerRef, bodyRef } = useScrollSync();
  const { okkoData, sortedCompetitors } = useMemo(() => {
    const reference = brands?.find(b => b.is_reference);
    const comps = brands?.filter(b => !b.is_reference) || [];

    const sorted = [...comps].sort((a: any, b: any) => (a.orderNumber || 999) - (b.orderNumber || 999));

    return {
      okkoData: reference,
      sortedCompetitors: sorted
    };
  }, [brands]);

  return (
    <div className="regional-table-scroll-container">
      <div className="regional-table-header-wrapper" ref={headerRef}>
        <div className="regional-table-header">
          <div className="regional-table-header-row">
            <div className={`regional-header-cell ${hideSales ? 'pricing-col-brand-wide' : 'pricing-col-brand'}`}>Бренд</div>
            {!hideSales && <div className="regional-header-cell pricing-col-sales">Реалізація л/доб.</div>}
            {fuelTypes.map(fuel => (
              <div key={`${regionKey}-header-${fuel}`} className="regional-header-cell pricing-col-fuel-regional">
                <div className="fuel-name">{fuel}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="regional-table-body-wrapper" ref={bodyRef}>
        <table className={`pricing-table regional-table ${isMobile ? 'mobile-table' : ''}`}>
          <colgroup>
            <col className={hideSales ? 'pricing-col-brand-wide' : 'pricing-col-brand'} />
            {!hideSales && <col className="pricing-col-sales" />}
            {fuelTypes.map(fuel => (
              <col key={fuel} className="pricing-col-fuel-regional" />
            ))}
          </colgroup>
          <thead className="regional-table-thead-hidden">
            <tr className="header-row">
              <th className={`header-cell ${hideSales ? 'pricing-col-brand-wide' : 'pricing-col-brand'}`}>Бренд</th>
              {!hideSales && <th className="header-cell pricing-col-sales">Реалізація л/доб.</th>}
              {fuelTypes.map(fuel => (
                <th key={`${regionKey}-header-${fuel}`} className="header-cell pricing-col-fuel-regional">
                  <div className="fuel-name">{fuel}</div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {okkoData && (
              <tr key={`${regionKey}-brand-${okkoData.brandId}`} className="data-row okko-row">
                <td className="data-cell brand-cell">
                  <span className="brand-name">{okkoData.brand}</span>
                </td>
                {!hideSales && (
                  <td className="data-cell sales-cell">
                    <SalesCell sales={okkoData.sales} />
                  </td>
                )}
                {fuelTypes.map(fuel => (
                  <td key={fuel} className="data-cell price-cell">
                    <PriceCell brand={okkoData} fuelType={fuel} hideDiscounts={true} formatPrice={formatPrice} hideSales={hideSales} priceType={priceType} />
                  </td>
                ))}
              </tr>
            )}

            {sortedCompetitors
              .map((competitor: any) => {
                const hasTooltipData = lastAuthor && lastUploadDatetime;
                const tooltipContent = hasTooltipData
                  ? `${lastAuthor}\n${formatToLocalDateTimeShort(lastUploadDatetime)}`
                  : '';

                const cellContent = (
                  <td
                    className="data-cell brand-cell-span"
                    colSpan={hideSales ? 1 : 2}
                    style={hasTooltipData ? { cursor: 'help' } : {}}
                  >
                    <span className="brand-name">{competitor.brand}</span>
                    <span className="station-count">
                      <span
                        className={`active-count ${
                          competitor.stationCounts.changeWithPrices === 'increase'
                            ? 'count-increased'
                            : competitor.stationCounts.changeWithPrices === 'decrease'
                              ? 'count-decreased'
                              : ''
                        }`}
                      >
                        {competitor.stationCounts.withPrices}
                      </span>
                      /
                      <span
                        className={`total-count ${
                          competitor.stationCounts.changeTotal === 'increase'
                            ? 'count-increased'
                            : competitor.stationCounts.changeTotal === 'decrease'
                              ? 'count-decreased'
                              : ''
                        }`}
                      >
                        {competitor.stationCounts.total}
                      </span>
                    </span>
                  </td>
                );

                return (
                  <tr key={`${regionKey}-brand-${competitor.brandId}`} className="data-row competitor-row">
                    {hasTooltipData ? (
                      <Tooltip
                        title={<div style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</div>}
                        placement="top"
                        arrow
                      >
                        {cellContent}
                      </Tooltip>
                    ) : (
                      cellContent
                    )}
                    {fuelTypes.map(fuel => (
                      <td key={fuel} className="data-cell price-cell">
                        <PriceCell brand={competitor} fuelType={fuel} hideDiscounts={true} formatPrice={formatPrice} priceType={priceType} />
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
